import { getSupabaseClient } from "../supabase/supabase";

const defaultProxyPath = "/api/dashboard";

// The dashboard proxy (src/server/dashboardProxy.ts) requires a valid Supabase
// session before injecting the server-side dashboard secret. Attach the current
// access token so authenticated CRM requests are authorized while anonymous
// callers are rejected.
async function dashboardAuthHeaders(): Promise<Record<string, string>> {
  try {
    const { data } = await getSupabaseClient().auth.getSession();
    const token = data.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export const isDashboardDataProviderEnabled = () =>
  ["aws", "dashboard", "gcp"].includes(
    import.meta.env.VITE_ECHOFLICKS_DATA_PROVIDER,
  );

export const getDashboardBaseUrl = () =>
  import.meta.env.VITE_ECHOFLICKS_DASHBOARD_PROXY_PATH ||
  import.meta.env.VITE_ECHOFLICKS_DASHBOARD_API_URL ||
  import.meta.env.VITE_ECHOFLICKS_AWS_PROXY_PATH ||
  import.meta.env.VITE_ECHOFLICKS_AWS_API_URL ||
  defaultProxyPath;

export const getDefaultDashboardMediaBucket = () =>
  import.meta.env.VITE_ECHOFLICKS_MEDIA_BUCKET || null;

export class DashboardRequestError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly details: Record<string, unknown> | null;
  readonly payload: Record<string, unknown> | null;

  constructor({
    status,
    message,
    code,
    details,
    payload,
  }: {
    status: number;
    message: string;
    code?: string | null;
    details?: Record<string, unknown> | null;
    payload?: Record<string, unknown> | null;
  }) {
    super(message);
    this.name = "DashboardRequestError";
    this.status = status;
    this.code = code ?? null;
    this.details = details ?? null;
    this.payload = payload ?? null;
  }
}

export async function dashboardRequest<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const response = await fetch(buildDashboardUrl(path), {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
      ...(await dashboardAuthHeaders()),
    },
  });

  const body = await response.text();
  const parsed = parseJsonBody(body);

  if (!response.ok) {
    throw dashboardRequestError(response.status, parsed, body);
  }

  return parsed as T;
}

export async function dashboardTextRequest(
  path: string,
  init: RequestInit = {},
): Promise<string> {
  const response = await fetch(buildDashboardUrl(path), {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      ...(await dashboardAuthHeaders()),
    },
  });
  const body = await response.text();
  if (!response.ok) {
    throw new Error(body || `Dashboard request failed with ${response.status}`);
  }
  return body;
}

export async function signDashboardMedia({
  bucket,
  key,
  expiresIn,
}: {
  bucket: string;
  key: string;
  expiresIn: number;
}) {
  return dashboardRequest<{
    url: string;
    expires_in: number;
    bucket: string;
    key: string;
  }>("/media/sign", {
    method: "POST",
    body: JSON.stringify({
      bucket,
      key,
      expires_in: expiresIn,
    }),
  });
}

function buildDashboardUrl(path: string) {
  const baseUrl = getDashboardBaseUrl().replace(/\/$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  if (baseUrl.startsWith("http://") || baseUrl.startsWith("https://")) {
    return `${baseUrl}${normalizedPath}`;
  }

  return `${baseUrl}${normalizedPath}`;
}

export const isAwsDashboardDataProviderEnabled = isDashboardDataProviderEnabled;
export const getAwsDashboardBaseUrl = getDashboardBaseUrl;
export const getDefaultAwsMediaBucket = getDefaultDashboardMediaBucket;
export const awsDashboardRequest = dashboardRequest;
export const awsDashboardTextRequest = dashboardTextRequest;
export const signAwsDashboardMedia = signDashboardMedia;

function parseJsonBody(body: string) {
  if (!body) return null;
  try {
    return JSON.parse(body);
  } catch {
    return null;
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function dashboardRequestError(
  status: number,
  parsed: unknown,
  body = "",
) {
  const payload = isObject(parsed) ? parsed : null;
  const nestedError = payload && isObject(payload.error) ? payload.error : null;
  const message =
    stringField(payload, "safe_operator_message") ??
    stringField(nestedError, "safe_operator_message") ??
    stringField(payload, "message") ??
    stringField(nestedError, "message") ??
    (typeof payload?.error === "string" ? payload.error : null) ??
    body ??
    `Dashboard request failed with ${status}`;
  const code =
    errorCode(payload) ??
    errorCode(nestedError) ??
    (typeof payload?.error === "string" ? payload.error : null);
  const details =
    (payload && isObject(payload.details) ? payload.details : null) ??
    (nestedError && isObject(nestedError.details) ? nestedError.details : null);

  return new DashboardRequestError({
    status,
    message: message || `Dashboard request failed with ${status}`,
    code,
    details,
    payload,
  });
}

function errorCode(value: Record<string, unknown> | null) {
  return (
    stringField(value, "code") ??
    stringField(value, "error_code") ??
    stringField(value, "reason_code")
  );
}

function stringField(value: Record<string, unknown> | null, key: string) {
  const field = value?.[key];
  return typeof field === "string" && field.trim() ? field : null;
}
